/**
 * Edit-log / version-history grid shared by the versioned admin datasets.
 *
 * Guidelines, DSCR pricing and RTL pricing all return the same log entry shape
 * (`{ at, by, action, section?, fromVersion, toVersion, note }`), and the
 * pricing-config screens return a `versionHistory` entry that maps onto it — so
 * one grid renders all of them. Optionally exposes a per-row Revert action.
 */
import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { ActionCell, actionColumn } from "./adminTableActions";

/**
 * Column defs for a log grid, exported so a screen can render the same columns
 * inside its own grid wrapper (the pricing screen puts them in a PricingGrid so
 * the log matches every other table there).
 */
export const buildEditLogColumnDefs = (onRevert, { asOf } = {}) => {
  const defs = [
    {
      headerName: "When",
      field: "at",
      width: 165,
      // Log timestamps are UTC datetimes — render in Eastern.
      valueFormatter: (p) => formatDateTime(p.value) || "—",
      sort: "desc",
    },
    // The sheet DAY a version reflects (DSCR daily sheet tracking) — a
    // date-only value, so formatDate, not the Eastern timestamp formatter.
    ...(asOf
      ? [
          {
            headerName: "Sheet as of",
            field: "asOfDate",
            width: 130,
            valueFormatter: (p) => formatDate(p.value, "—", "MMM d, yyyy"),
          },
        ]
      : []),
    { headerName: "By", field: "by", minWidth: 180, flex: 1 },
    { headerName: "Action", field: "action", width: 120 },
    {
      headerName: "Section",
      field: "section",
      width: 150,
      valueFormatter: (p) => p.value || "—",
    },
    {
      headerName: "Version",
      colId: "version",
      width: 110,
      cellClass: "key",
      valueGetter: (p) =>
        p.data?.fromVersion != null
          ? `v${p.data.fromVersion} → v${p.data.toVersion}`
          : `v${p.data?.toVersion ?? "—"}`,
    },
    { headerName: "Note", field: "note", minWidth: 220, flex: 1.6 },
  ];
  if (onRevert) {
    defs.push(
      actionColumn({
        colId: "revert",
        // "Revert to" is a word, so this column is sized for text rather than
        // the square-per-control default.
        width: 110,
        cellRenderer: (p) =>
          p.data?.toVersion == null ? null : (
            <ActionCell>
              <Button
                size="small"
                variant="text"
                onClick={() => onRevert(p.data.toVersion, p.data)}
                sx={{ px: 1, fontSize: 12.5, lineHeight: 1.2 }}
              >
                Revert to
              </Button>
            </ActionCell>
          ),
      }),
    );
  }
  return defs;
};

export const editLogRowId = (p) =>
  `${p.data?.at || ""}-${p.data?.toVersion ?? ""}-${p.data?.section || ""}`;

export const EditLogTable = ({ rows, onRevert, height = "260px" }) => {
  const columnDefs = useMemo(() => {
    const defs = [
      {
        headerName: "When",
        field: "at",
        width: 165,
        // Log timestamps are UTC datetimes — render in Eastern.
        valueFormatter: (p) => formatDateTime(p.value) || "—",
        sort: "desc",
      },
      { headerName: "By", field: "by", minWidth: 180, flex: 1 },
      { headerName: "Action", field: "action", width: 120 },
      {
        headerName: "Section",
        field: "section",
        width: 150,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Version",
        colId: "version",
        width: 110,
        valueGetter: (p) =>
          p.data?.fromVersion != null
            ? `v${p.data.fromVersion} → v${p.data.toVersion}`
            : `v${p.data?.toVersion ?? "—"}`,
      },
      { headerName: "Note", field: "note", minWidth: 220, flex: 1.6 },
    ];
    if (onRevert) {
      defs.push({
        headerName: "",
        colId: "revert",
        width: 110,
        sortable: false,
        cellRenderer: (p) =>
          p.data?.toVersion == null ? null : (
            <Button
              size="small"
              variant="text"
              onClick={() => onRevert(p.data.toVersion, p.data)}
            >
              Revert to
            </Button>
          ),
      });
    }
    return defs;
  }, [onRevert]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    }),
    [],
  );

  const getRowId = useCallback(
    (p) => `${p.data?.at || ""}-${p.data?.toVersion ?? ""}-${p.data?.section || ""}`,
    [],
  );

  return (
    <AgGridTableWrapper compactTable height={height}>
      <AgGridReact
        rowData={rows || []}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
      />
    </AgGridTableWrapper>
  );
};

EditLogTable.propTypes = {
  rows: PropTypes.array,
  onRevert: PropTypes.func,
  height: PropTypes.string,
};

export default EditLogTable;
